import { useTranslation } from 'react-i18next';
import type { FilterFieldOption } from '~/components/ui';
import type { TradePerspective } from '~/graphql/graphql';
import { textValueOf } from '~/types/filters';
import {
  activeFilterGroups,
  type OrderFilterRow,
} from '~/pages/orders/lib/orderFilters';

type OrdersFilterReadoutProps = {
  readonly rows: OrderFilterRow[];
  readonly fieldOptions: FilterFieldOption[];
  readonly perspective: TradePerspective;
};

export function OrdersFilterReadout({
  rows,
  fieldOptions,
  perspective,
}: OrdersFilterReadoutProps) {
  const { t } = useTranslation('order');

  const groups = activeFilterGroups(rows, perspective);

  if (groups.length === 0) {
    return null;
  }

  const labelFor = (field: string) =>
    fieldOptions.find((option) => option.field === field)?.label ?? field;

  const operatorLabelFor = (field: string, operator: string) =>
    fieldOptions
      .find((option) => option.field === field)
      ?.operatorOptions.find((candidate) => candidate.operator === operator)
      ?.label ?? operator;

  // A select row stores the enum value; the sentence must read the same words
  // the dropdown showed.
  const valueLabelFor = (field: string, value: string) =>
    fieldOptions
      .find((option) => option.field === field)
      ?.selectOptions?.find((candidate) => candidate.value === value)?.label ??
    value;

  return (
    <div
      data-testid="orders-filter-readout"
      className="text-sm text-neut-700 border-l-2 border-prim pl-3 py-1"
    >
      {groups.map((group, groupIndex) => (
        <span key={group[0].row.id}>
          {groupIndex > 0 && (
            <span
              data-testid="readout-join-or"
              className="mx-2 text-xs font-bold uppercase tracking-wide text-neut-400"
            >
              {t('order.filter.or')}
            </span>
          )}
          {group.map(({ row }, rowIndex) => (
            <span key={row.id}>
              {rowIndex > 0 && (
                <span
                  data-testid="readout-join-and"
                  className="mx-2 text-xs font-bold uppercase tracking-wide text-neut-400"
                >
                  {t('order.filter.and')}
                </span>
              )}
              <b>{labelFor(row.field)}</b>{' '}
              <span className="text-prim">
                {operatorLabelFor(row.field, row.operator)}
              </span>{' '}
              {valueLabelFor(row.field, textValueOf(row).trim())}
            </span>
          ))}
        </span>
      ))}
    </div>
  );
}
